/**
 * 메인 지도 컴포넌트
 * 
 * 주요 기능:
 * 1. 카카오맵을 표시하고 관리
 * 2. 도로 교통 정보 표시 (TrafficLine)
 * 3. 지도 타입 토글 (일반/위성)
 * 4. 마커 및 오버레이 관리
 * 
 * 상태 관리:
 * - mapParamState: 지도 중심점, 줌 레벨, 경계값 등 지도 설정
 * - searchParamState: 검색 관련 파라미터
 * - calendarState: 날짜 선택 관련 상태
 * - polyLineClickState: 도로 클릭 시 표시되는 팝업 정보
 * 
 * 주요 이벤트:
 * - onZoomChanged: 줌 레벨 변경 시 지도 파라미터 업데이트
 * - onDragEnd: 지도 드래그 종료 시 지도 파라미터 업데이트
 */
import React, { useEffect, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  calendarState,
  mapParamState,
  polyLineClickState,
  searchParamState,
} from "../../atoms";
import Popup from "./component/Popup";
import TrafficLine from "./component/TrafficLine";
import { format } from "date-fns";
import { Marker } from "../../interface/map";
import Map from "./component/Map";
import CustomOverlayMap from "./component/CustomOverlayMap";
import MapMarker from "./component/MapMarker";
import MapTypeToggle from "./component/MapTypeToggle";
import { marker } from "leaflet";

const MainMap = () => {
  const [param, setParam] = useRecoilState(mapParamState);
  const [markerInstance, setMarkerInstance] =
    useState<kakao.maps.Marker | null>(null);
  const searchParam = useRecoilValue(searchParamState);
  const date = useRecoilValue(calendarState);
  const polyLineClick = useRecoilValue(polyLineClickState);
  useEffect(() => {
    setParam((prev) => ({
      ...prev,
      startDate: format(new Date(date.startDate), "yyyy-MM-dd"),
      endDate: format(new Date(date.endDate), "yyyy-MM-dd"),
    }));
  }, [date.startDate, date.endDate, setParam]);

  const mapEvent = (map: kakao.maps.Map) => {
    setParam((prevParam: any) => ({
      ...prevParam,
      zoom: map.getLevel(),
      bounds: [
        {
          lat: map.getBounds().getNorthEast().getLat(),
          lng: map.getBounds().getNorthEast().getLng(),
        },
        {
          lat: map.getBounds().getSouthWest().getLat(),
          lng: map.getBounds().getSouthWest().getLng(),
        },
      ],
    }));
  };
  const [map, setMap] = useState<kakao.maps.Map | null>(null);
  const [info, setInfo] = useState<Marker>();
  const [markers, setMarkers] = useState<Marker[]>([]);
  useEffect(() => {
    if (!map) return;
    if (!searchParam.place) {
      if (markerInstance) {
        markerInstance.setMap(null); // 지도에서 마커 제거
        setMarkerInstance(null); // 상태 초기화
      }
      return;
    }
    const geocoder = new kakao.maps.services.Geocoder();

    geocoder.addressSearch(searchParam.place, function (result, status) {
      // 정상적으로 검색이 완료됐으면
      if (status === kakao.maps.services.Status.OK) {
        var coords = new kakao.maps.LatLng(
          Number(result[0].y),
          Number(result[0].x),
        );

        if (markerInstance) {
          markerInstance.setMap(null);
        }

        const newMarker = new kakao.maps.Marker({
          map: map,
          position: coords,
        });

        setMarkerInstance(newMarker);

        map.setCenter(coords);
      }
    });
  }, [map, searchParam.place]);

  const handleMarkerClick = (marker: any) => {
    map?.setCenter(
      new kakao.maps.LatLng(marker.position.lat, marker.position.lng),
    );
    setTimeout(() => {
      map?.setLevel(3, {
        anchor: new kakao.maps.LatLng(marker.position.lat, marker.position.lng),
        animate: {
          duration: 300,
        },
      });
    }, 500);
  };

  return (
    <Map
      center={param.center}
      level={param.zoom}
      className="w-screen grow"
      onZoomChanged={(map: any) => mapEvent(map)}
      onDragEnd={(map: any) => mapEvent(map)}
      maxLevel={13}
      onCreate={setMap}
    >
      <MapTypeToggle map={map} />
      <TrafficLine />
      {polyLineClick && (
        <CustomOverlayMap position={polyLineClick.position}>
          <Popup />
        </CustomOverlayMap>
      )}
      {markers.map((marker, index) => (
        <MapMarker
          key={`MapMarker(${index})-${marker.position.lat}:${marker.position.lng}`}
          position={marker.position}
          onMouseOver={() => setInfo(marker)}
          onClick={() => handleMarkerClick(marker)}
        >
          {
            // info && info.content === marker.content && marker.content
            info && info.content === marker.content && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                  fontWeight: "bold",
                  padding: "10px",
                  maxWidth: "500px",
                  height: "60px",
                  color: "#000",
                  whiteSpace: "nowrap",
                }}
              >
                <p>{marker.content}</p>
              </div>
            )
          }
        </MapMarker>
      ))}
    </Map>
  );
};

export default MainMap;
